import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

import type { ClientCategory } from '@/features/categories/types/category';

export const CLAIM_REFERENCE_LENGTH_LIMIT = 100;

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/**
 * Check that a claim reference is invalid.
 *
 * @param claimReferenceInput - Claim reference name provided be user.
 * @param regex - Apply provided regex if provided.
 * @returns If the claim reference is invalid.
 */
export function isClaimReferenceInvalid(
  claimReferenceInput: string,
  regex?: string,
): boolean {
  const claimReferenceName = claimReferenceInput.trim();

  /** Check if claim reference has any input. */
  if (!claimReferenceName) {
    return true;
  }

  /** Check if claim reference is within length limits. */
  if (claimReferenceName.length > CLAIM_REFERENCE_LENGTH_LIMIT) {
    return true;
  }

  /**
   * Check if claim reference matches possible regex.
   *
   * The pattern is supplied by the API, so it may not compile. Constructing it
   * unguarded throws a SyntaxError out of a plain validation helper. Skip the
   * check instead, leaving the character and length rules below to apply,
   * rather than locking every user out over a bad server-side config.
   */
  if (regex) {
    try {
      if (!new RegExp(regex).test(claimReferenceName)) {
        return true;
      }
    } catch {
      // Malformed pattern: fall through to the remaining checks.
    }
  }

  /** Check if claim reference contains only allowed ASCII characters. */
  const allowedCharsRegex = /^[a-zA-Z0-9@\-_ .!]+$/;
  if (!allowedCharsRegex.test(claimReferenceName)) {
    return true;
  }

  return false;
}

/**
 * Replaces placeholders in a string with values from a map.
 *
 * @remarks
 * This function replaces placeholders in a string with values from a map.
 * Placeholders are in the format \{placeholder\}. The function replaces each
 * placeholder with the corresponding value from the map. If a placeholder
 * does not have a corresponding value in the map, it is replaced with an
 * empty string. The function returns the formatted string. Note, that this function
 * should only be used if the translation text does not support the standard
 * i18next interpolation.
 *
 * @param template - The string containing placeholders in the format \{placeholder\}.
 * @param valuesMap - A map of key-value pairs to replace the placeholders.
 * @returns The formatted string with placeholders replaced by corresponding values.
 */
export function fillPlaceholders(
  template: string,
  valuesMap: Record<string, string>,
): string {
  return template.replace(/\{(\w+)\}/g, (_, key) => {
    return valuesMap[key] ?? '';
  });
}

/**
 * Get the category image URL.
 *
 * @param category - The category ID.
 * @returns The category image URL or default URL.
 */
export function getCategoryImageUrl(category?: ClientCategory) {
  const cid = category?.cid || 0;

  return `${import.meta.env.VITE_CATEGORY_IMAGE_URL}/${cid}.jpg`;
}

/**
 * Round number if it has a decimal part.
 *
 * @param num  - The number to round.
 * @returns The rounded number if it has a decimal part, otherwise the original number.
 */
export function roundIfDecimal(num: number) {
  if (Number.isInteger(num)) return num;
  return Math.round(num * 100) / 100;
}
